import { ReactElement, useEffect, useState } from 'react'
//import { Reepay } from '@raskrask-dk/rr-components'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getPaymentInfo } from '../../redux/reducers/checkoutSettings'
import { useBFSM, useBFSMCanDo } from '../../contexts/bfsmContext'
import {
    CheckoutActions,
    BookingReepayInit,
    ProductType,
    CheckoutState,
    DetailsState,
    GeneralActions,
    CalendarState,
} from '@raskrask-dk/bookingflow-state-machine'
import { CheckoutTemplate } from '.'
import { useCheckout } from './hooks'
import { BfsmRepoAdaptors } from '../../adaptors/bfsmRepoAdaptors'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import ReactPixel from 'react-facebook-pixel'
import { v4 as uuidv4 } from 'uuid'
import { setErrorcase } from '../../redux/reducers/appSettings'
import { RedirectionToConfirmation } from '../../services/confirmationredirect'
// import Reepay from '../chemistry/reepay'

import { Button, SVG, SVGs } from '@raskrask-dk/rr-components'
import { useTranslation } from 'react-i18next'

export interface ReepayProps {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    onAccept?: () => void
    onCancel?: () => void
    onClose?: () => void
    showCloseButton?: boolean
    token: string
}

export type Status = 'idle' | 'loading' | 'ready' | 'error'
export type ScriptElt = HTMLScriptElement | null

function useScript(src: string): Status {
    const [status, setStatus] = useState<Status>(src ? 'loading' : 'idle')

    useEffect(() => {
        if (!src) {
            setStatus('idle')
            return
        }
        let script: ScriptElt = document.querySelector(`script[src="${src}"]`)

        if (!script) {
            script = document.createElement('script')
            script.src = src
            script.async = true
            script.setAttribute('data-status', 'loading')
            document.body.appendChild(script)

            const setAttributeFromEvent = (event: Event) => {
                script?.setAttribute(
                    'data-status',
                    event.type === 'load' ? 'ready' : 'error'
                )
            }

            script.addEventListener('load', setAttributeFromEvent)
            script.addEventListener('error', setAttributeFromEvent)
        } else {
            setStatus(script.getAttribute('data-status') as Status)
        }

        const setStateFromEvent = (event: Event) => {
            setStatus(event.type === 'load' ? 'ready' : 'error')
        }

        script.addEventListener('load', setStateFromEvent)
        script.addEventListener('error', setStateFromEvent)

        return () => {
            if (script) {
                script.removeEventListener('load', setStateFromEvent)
                script.removeEventListener('error', setStateFromEvent)
            }
        }
    }, [src])

    return status
}

const CheckoutPage = (): ReactElement => {
    const [BFSM] = useBFSM()

    const paymentInfo = useAppSelector((state) => getPaymentInfo(state))

    const product = BFSM.getData<DetailsState>().product

    const isBooking = product.type === 'Booking'
    const isPackage = product.type === 'Package'

    const [antiSpam, setAntiSpam] = useState(false)
    const [bookingId, setbookingId] = useState<string>('')
    const [isOpen, setIsOpen] = useState(false)
    const [, setrpOrderHandle] = useState<string>('')
    const [rptoken, setrptoken] = useState<string>('')
    const [hasFinalizeErrored, setHasFinalizeErrored] = useState<boolean>(false)

    const calendarData = BFSM.getData<CalendarState>()
    const dispatch = useAppDispatch()
    const data = BFSM.getData<CheckoutState>()
    const productType = data.product.type
    const customerType = data.customer.type
    const basisType = calendarData.basisType
    const [paymentMethod, setPaymentMethod] = useBFSMValue<
        CheckoutState,
        CheckoutActions.ChoosePaymentMethod,
        'paymentMethod'
    >('paymentMethod', CheckoutActions.ChoosePaymentMethod)
    const canBook = useBFSMCanDo(CheckoutActions.Next)

    useEffect(() => {
        if (!paymentMethod) {
            setPaymentMethod('Mobile Pay')
        }
    }, [setPaymentMethod, paymentMethod])

    const { shouldShowPaymentMethods } = useCheckout(BFSM)
    const finalize = async (): Promise<void> => {
        try {
            const purchaseData = BfsmRepoAdaptors.convertToFinalizeProductDTO(
                data.customer,
                data.product,
                true,
                data.paymentMethod,
                data.promoCode,
                data.voucher,
                (data as any).bookingReasons
            )
            const response = await BFSM.repo.finalizeProduct(purchaseData)

            if (
                (response as { message: string })?.message ===
                'Overlapping Booking found'
            ) {
                dispatch(setErrorcase('OverlappingError'))
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            }

            if ((response as { message: string })?.message === 'Success') {
                await proceedToConfirmation() //no payment was done -> no affilities
                return
            }
            const reepayResponse = response as BookingReepayInit
            if (reepayResponse?.session_ID !== '') {
                if (reepayResponse.bookingId) {
                    setbookingId(reepayResponse.bookingId)
                }
                setrpOrderHandle(reepayResponse.orderHandle)
                setrptoken(reepayResponse.session_ID)
                setIsOpen(true)
            }
            // If the else is hit then an error must've happened.
            else {
                setHasFinalizeErrored(true)
            }
        } catch (err) {
            dispatch(setErrorcase('FinalizeError'))
            BFSM.doAction(GeneralActions.GoToErrorState, undefined)
        }
    }

    const onClickBook = (): void => {
        if (antiSpam) return
        if (!antiSpam) setAntiSpam(true)
        finalize().then(() => {
            setAntiSpam(false)
        })
        ReactPixel.track('AddPaymentInfo')
    }

    // https://www.partner-ads.com/dk/tracking_lead.txt
    const GetPartnerAdCookie = (cname: string): string => {
        const name = cname + '='
        const ca = document.cookie.split(';')
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
            while (c.charAt(0) == ' ') {
                c = c.substring(1)
            }
            if (c.indexOf(name) == 0) return c.substring(name.length, c.length)
        }
        return ''
    }

    const proceedToConfirmation = async (): Promise<void> => {
        if (productType === 'Package') {
            data.address.address
            return RedirectionToConfirmation.confirmation_package(
                data.address.address,
                data.customer.type,
                (data as any).basisType.id,
                data.product.service.goal.id,
                data.product.service.id,
                (data as any).product.partner.id,
                data.customer.name,
                data.customer.phone,
                data.customer.email
            )
        }
        const serviceName = data.product.service.name
        const partnerType =
            basisType.id == 1
                ? 'massør'
                : basisType.id == 1
                ? 'personlig træner'
                : 'yoga lærer'
        const alias = calendarData.partner?.alias || ''
        const price = data.priceToPay ? `${data.priceToPay}` : '0'
        const serviceDate = `${calendarData.date?.toString('yyyy-MM-dd')} ${
            calendarData.time
        }`
        const firstName = data.customer.name.split('')[0]
        if (customerType === 'Company' && productType === 'Booking')
            return RedirectionToConfirmation.confirmation_company(
                serviceName,
                partnerType,
                alias,
                price,
                serviceDate,
                firstName
            )
        if (customerType === 'Private' && basisType?.id === 1)
            return RedirectionToConfirmation.confirmation_massage(
                serviceName,
                partnerType,
                alias,
                price,
                serviceDate,
                firstName
            )
        if (customerType === 'Private' && basisType?.id === 2)
            return RedirectionToConfirmation.confirmation_traening(
                serviceName,
                partnerType,
                alias,
                price,
                serviceDate,
                firstName
            )
        if (customerType === 'Private' && basisType?.id === 3)
            return RedirectionToConfirmation.confirmation_yoga(
                serviceName,
                partnerType,
                alias,
                price,
                serviceDate,
                firstName
            )
    }

    const submitAffiliatesTracking = async (
        productType: ProductType
    ): Promise<void> => {
        try {
            const paid = GetPartnerAdCookie('paid') || undefined
            const pacid = GetPartnerAdCookie('pacid') || undefined
            if (
                productType === 'Package' ||
                paid === undefined ||
                pacid === undefined
            )
                return

            if (
                productType === 'Booking' &&
                paymentInfo?.turnover !== undefined
            ) {
                await BFSM.repo.submitPartnerAdsSale({
                    paid: paid,
                    pacid: pacid,
                    ordernummer: bookingId,
                    omsaetningPRSalg: paymentInfo.turnover,
                })
            }

            if (productType === 'CompanyLead') {
                await BFSM.repo.submitPartnerAdsLead({
                    paid: paid,
                    pacid: pacid,
                    ordernummer: uuidv4(),
                })
            }
        } catch (err) {
            return
        }
    }

    // ___________________________________________________________________

    const { t } = useTranslation()
    const [reepayModal, setReepayModal] = useState<any>(null)
    const status = useScript('https://checkout.reepay.com/checkout.js')

    useEffect(() => {
        if (status === 'ready') {
            setReepayModal(new window.Reepay.ModalCheckout())
        }

        return () => {
            if (status === 'ready' && reepayModal) {
                reepayModal.destroy()
                setReepayModal(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status])

    useEffect(() => {
        if (isOpen && rptoken) {
            const newReepayModal = new window.Reepay.ModalCheckout(rptoken)
            setReepayModal(newReepayModal)
            newReepayModal.addEventHandler(window.Reepay.Event.Accept, () => {
                submitAffiliatesTracking(data.product.type)
                proceedToConfirmation()
            })
            newReepayModal.addEventHandler(window.Reepay.Event.Cancel, () => {
                setIsOpen(false)
            })
            newReepayModal.addEventHandler(window.Reepay.Event.Close, () => {
                setIsOpen(false)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, rptoken])

    if (!paymentInfo) return <></>
    return (
        <CheckoutTemplate
            antiSpam={antiSpam}
            canBook={canBook}
            isBooking={isBooking}
            isPackage={isPackage}
            onClickBook={onClickBook}
            paymentInfo={paymentInfo}
            shouldShowPaymentMethods={shouldShowPaymentMethods}
            hasFinalizeErrored={hasFinalizeErrored}
        >
            {isOpen && paymentMethod === 'Mobile Pay' && (
                <Button
                    css={{
                        bgc: '#F5F5F2',
                        bottom: 64,
                        justifyContent: 'left',
                        left: '50%',
                        maxW: 550,
                        position: 'absolute',
                        transform: 'translateX(-50%)',
                        w: 'calc(100% - 32px)',
                        zIndex: 10000,
                    }}
                    onClick={() => {
                        //reepayModal.close()
                        reepayModal.destroy()
                        setIsOpen(false)
                    }}
                    startIcon={<SVG src={SVGs.ChevronLeft} />}
                    size="large"
                    variant="plain"
                >
                    {t('checkout.payment.closebtn')}
                </Button>
            )}
        </CheckoutTemplate>
    )
}

export default CheckoutPage
